import { useMemo } from "react";
import { useDispatch } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import { ConnectProto } from "@racoon/api";
import {
  orgGrpcApi,
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation,
} from "~/shared/api/organization/organization.grpc";
import type { RootState } from "~/shared/store/store";
import { RuleDraft, setOptOut, toDraft, toggleChannel, toRules } from "./notification-rules";

/**
 * Notification rules of one audience. Every change is saved at once and shown
 * optimistically; a failed save refetches the server state (GlobalErrorToaster shows why).
 */
export const useNotificationSettings = (
  selectedCompanyId: number | undefined,
  audience: ConnectProto.UserRole,
) => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, UnknownAction>>();

  // Settings resolve to the current user's own org (gRPC getSetting, auth context).
  // GetSetting always returns the effective rules, defaults included.
  const { data: organizationSettings, isLoading } = useGetOrganizationSettingsQuery(
    {},
    { skip: !selectedCompanyId },
  );
  const [updateSettings, { isLoading: isSaving }] = useUpdateOrganizationSettingsMutation();

  const serverRules = useMemo(
    () => organizationSettings?.notificationConfig?.rules?.rules ?? [],
    [organizationSettings],
  );
  const rules = useMemo(() => toDraft(serverRules, audience), [serverRules, audience]);

  const save = async (next: RuleDraft) => {
    if (!organizationSettings) return;
    // UpdateSetting overwrites the whole config, so the legacy log_types ride along.
    const notificationConfig: ConnectProto.NotificationConfig = {
      $typeName: "raccoonai.NotificationConfig",
      logTypes: organizationSettings.notificationConfig?.logTypes ?? [],
      rules: {
        $typeName: "raccoonai.NotificationRules",
        rules: toRules(serverRules, audience, next),
      },
    };
    dispatch(
      orgGrpcApi.util.updateQueryData("getOrganizationSettings", {}, (cached) => {
        cached.notificationConfig = notificationConfig;
      }),
    );
    try {
      await updateSettings({ id: organizationSettings.id, notificationConfig }).unwrap();
    } catch {
      dispatch(orgGrpcApi.util.invalidateTags(["OrgSettings"]));
    }
  };

  return {
    rules,
    // No settings (still loading, skipped or failed): nothing to edit or save against.
    isReady: !!organizationSettings,
    isLoading,
    // UpdateSetting overwrites the whole config, so saves must not overlap.
    isSaving,
    toggleChannel: (
      event: ConnectProto.NotificationEvent,
      channel: ConnectProto.NotificationChannel,
    ) => save(toggleChannel(rules, event, channel)),
    setOptOut: (event: ConnectProto.NotificationEvent, optOutAllowed: boolean) =>
      save(setOptOut(rules, event, optOutAllowed)),
  };
};
